/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-15
 */

import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useBulkUpdateItemsMutation,
  ShoppingItem,
} from '../../../api/shoppingListApi';

interface ItemManagementTableProps {
  storeId: string;
  isShoppingListView?: boolean;
  reverseOrder?: boolean;
}

/**
 * Item management table component.
 * Displays editable table of shopping items with inline add/edit/delete functionality.
 */
const ItemManagementTable: React.FC<ItemManagementTableProps> = ({
  storeId,
  isShoppingListView = false,
  reverseOrder = false,
}) => {
  const { data: allItems = [], isLoading, error } = useGetItemsQuery(storeId);

  // Search filter state
  const [searchFilter, setSearchFilter] = useState('');

  // Filter and sort items based on mode
  const items = React.useMemo(() => {
    let filtered = isShoppingListView
      ? allItems.filter(item => item.is_on_list)
      : allItems;

    // Apply search filter
    if (searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const name = item.name.toLowerCase();
        const location = (item.location || '').toLowerCase();
        const price = (item.price || '').toString();

        return (
          name.includes(searchLower) ||
          location.includes(searchLower) ||
          price.includes(searchLower)
        );
      });
    }

    // Sort based on view mode
    return [...filtered].sort((a, b) => {
      if (isShoppingListView) {
        // Shopping list view: sort by checked status then location
        // First sort by checked status (unchecked items first)
        if (a.is_checked_off !== b.is_checked_off) {
          return a.is_checked_off ? 1 : -1;
        }

        // Then sort by location
        const locationA = a.location || '';
        const locationB = b.location || '';

        if (reverseOrder) {
          return locationB.localeCompare(locationA);
        }
        return locationA.localeCompare(locationB);
      }
      // Item management view: sort by name
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allItems, isShoppingListView, reverseOrder, searchFilter]);
  const [createItem, { isLoading: isCreating }] = useCreateItemMutation();
  const [updateItem] = useUpdateItemMutation();
  const [bulkUpdateItems] = useBulkUpdateItemsMutation();
  const [deleteItem] = useDeleteItemMutation();

  // New item form state
  const [newItemName, setNewItemName] = useState('');
  const [newItemLocation, setNewItemLocation] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemQuantity, setNewItemQuantity] = useState('1');

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{
    name: string;
    location: string;
    price: string;
    quantity: string;
  }>({
    name: '',
    location: '',
    price: '',
    quantity: '1',
  });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  // Clear all checkboxes dialog state
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);

  // Error handling state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasNameError, setHasNameError] = useState(false);

  const handleAddItem = async () => {
    if (!newItemName.trim()) {
      setErrorMessage('Item name is required.');
      setHasNameError(true);
      return;
    }

    if (!storeId) {
      console.error('No store selected');
      setErrorMessage('No store selected. Please select a store first.');
      return;
    }

    try {
      await createItem({
        store: storeId,
        name: newItemName.trim(),
        location: newItemLocation.trim(),
        price: newItemPrice || undefined,
        quantity: parseInt(newItemQuantity, 10) || 1,
        is_on_list: true, // Default to checked (on shopping list)
      }).unwrap();

      // Reset form and clear errors on success
      setNewItemName('');
      setNewItemLocation('');
      setNewItemPrice('');
      setNewItemQuantity('1');
      setErrorMessage(null);
      setHasNameError(false);
    } catch (err: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      console.error('Failed to create item:', err);
      console.error('Error details:', {
        status: err?.status,
        data: err?.data,
        message: err?.message,
      });

      setHasNameError(false); // Reset, will be set if name-specific error

      // Handle different error types
      if (err?.status === 400) {
        // Validation errors
        if (err?.data?.name && Array.isArray(err.data.name)) {
          setErrorMessage(
            `Item name error: ${err.data.name[0]} Please use a different name or edit the existing item.`
          );
          setHasNameError(true);
        } else if (err?.data?.detail) {
          setErrorMessage(err.data.detail);
        } else if (
          err?.data?.non_field_errors &&
          Array.isArray(err.data.non_field_errors)
        ) {
          setErrorMessage(err.data.non_field_errors[0]);
          if (
            err.data.non_field_errors[0]
              .toLowerCase()
              .includes('already exists')
          ) {
            setHasNameError(true);
          }
        } else {
          setErrorMessage(
            `An item named "${newItemName.trim()}" already exists in this store. Please use a different name or edit the existing item.`
          );
          setHasNameError(true);
        }
      } else if (err?.status === 401 || err?.status === 403) {
        // Authentication errors
        setErrorMessage(
          'You are not authorized to add items. Please log in again.'
        );
      } else if (err?.status === 429) {
        // Rate limiting
        setErrorMessage(
          'Too many requests. Please wait a moment and try again.'
        );
      } else if (err?.status >= 500) {
        // Server errors
        setErrorMessage(
          'Server error. Please try again later or contact support if the problem persists.'
        );
      } else if (err?.name === 'NetworkError' || !err?.status) {
        // Network errors
        setErrorMessage(
          'Unable to connect to the server. Please check your internet connection and try again.'
        );
      } else {
        // Generic error
        setErrorMessage(
          'Failed to add item. Please try again or contact support if the problem persists.'
        );
      }
    }
  };

  const handleCheckboxChange = async (item: ShoppingItem) => {
    try {
      if (isShoppingListView) {
        // In shopping list view, toggle is_checked_off
        await updateItem({
          itemId: item.id,
          data: { is_checked_off: !item.is_checked_off },
        }).unwrap();
      } else {
        // In item management view, toggle is_on_list
        await updateItem({
          itemId: item.id,
          data: { is_on_list: !item.is_on_list },
        }).unwrap();
      }
    } catch (err) {
      console.error('Failed to update item:', err);
    }
  };

  const handleOpenClearAllDialog = () => {
    setClearAllDialogOpen(true);
  };

  const handleClearAllCheckboxes = async () => {
    let itemsToUpdate;
    if (isShoppingListView) {
      itemsToUpdate = allItems.filter(item => item.is_checked_off);
    } else {
      itemsToUpdate = allItems.filter(item => item.is_on_list);
    }

    if (itemsToUpdate.length === 0) {
      setClearAllDialogOpen(false);
      return;
    }

    const itemUpdates = itemsToUpdate.map(item => ({
      id: item.id,
      ...(isShoppingListView
        ? { is_checked_off: false }
        : { is_on_list: false }),
    }));

    try {
      await bulkUpdateItems({
        data: { item_updates: itemUpdates },
        storeId,
      }).unwrap();
    } catch (err) {
      console.error('Failed to clear all checkboxes:', err);
    } finally {
      setClearAllDialogOpen(false);
    }
  };

  const handleEdit = (item: ShoppingItem) => {
    setEditingId(item.id);
    setEditValues({
      name: item.name,
      location: item.location,
      price: item.price || '',
      quantity: item.quantity.toString(),
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    try {
      await updateItem({
        itemId: editingId,
        data: {
          name: editValues.name.trim(),
          location: editValues.location.trim(),
          price: editValues.price || null,
          quantity: parseInt(editValues.quantity, 10) || 1,
        },
      }).unwrap();

      setEditingId(null);
    } catch (err) {
      console.error('Failed to update item:', err);
    }
  };

  const handleDelete = (itemId: string) => {
    setItemToDelete(itemId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      try {
        await deleteItem({ itemId: itemToDelete, storeId }).unwrap();
      } catch (err) {
        console.error('Failed to delete item:', err);
      }
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const calculateTotal = () =>
    items
      .reduce((total, item) => {
        const price = parseFloat(item.price || '0');
        return total + price * item.quantity;
      }, 0)
      .toFixed(2);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load items. Please try again.</Alert>
    );
  }

  const checkedCount = isShoppingListView
    ? items.filter(item => !item.is_checked_off).length
    : items.filter(item => item.is_on_list).length;

  const completedCount = isShoppingListView
    ? items.filter(item => item.is_checked_off).length
    : 0;

  return (
    <Paper
      sx={{
        // Add bottom margin on mobile to prevent footer from covering content
        mb: { xs: 2, sm: 0 },
      }}
    >
      <Box
        p={{ xs: 1, sm: 1.5, md: 2 }}
        sx={{
          // Add bottom padding on mobile to ensure add item form is visible
          pb: { xs: 12, sm: 1.5, md: 2 },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={{ xs: 1, sm: 1.5, md: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
          >
            {isShoppingListView ? 'Shopping List Items' : 'Shopping Items'}
          </Typography>
          <Box
            display="flex"
            gap={{ xs: 0.5, sm: 1, md: 2 }}
            alignItems="center"
            flexWrap="wrap"
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' } }}
            >
              {isShoppingListView
                ? `${checkedCount} remaining • ${completedCount} completed • $${calculateTotal()}`
                : `${checkedCount} item${checkedCount !== 1 ? 's' : ''} • $${calculateTotal()}`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={handleOpenClearAllDialog}
              disabled={
                isShoppingListView ? completedCount === 0 : checkedCount === 0
              }
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                minWidth: 'auto',
                px: 1,
                py: 0.25,
              }}
            >
              Clear All
            </Button>
          </Box>
        </Box>

        {/* Error Alert - prominently displayed above table */}
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => {
              setErrorMessage(null);
              setHasNameError(false);
            }}
            sx={{ mb: { xs: 1, sm: 1.5, md: 2 } }}
          >
            <strong>Error:</strong> {errorMessage}
          </Alert>
        )}

        {/* Search Filter */}
        <Box mb={{ xs: 1, sm: 1.5, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name, location, or price..."
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              },
            }}
          />
        </Box>

        <TableContainer>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                px: { xs: 0.25, sm: 0.75, md: 1.5 },
                py: { xs: 0.25, sm: 0.75, md: 1 },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="none"
                  sx={{
                    width: { xs: 24, sm: 32, md: 44 },
                    px: { xs: 0, sm: 0, md: 0.25 },
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    {isShoppingListView ? 'Done' : 'Add'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: { xs: '30%', sm: '25%' } }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    Name
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: { xs: '30%', sm: '25%' } }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    Location
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: '15%',
                    display: { xs: 'none', sm: 'table-cell' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    Price
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: { xs: '12%', sm: '8%' } }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    Qty
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    width: { xs: '15%', sm: '15%' },
                    display: { xs: 'none', sm: 'table-cell' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    Subtotal
                  </Typography>
                </TableCell>
                <TableCell
                  padding="none"
                  sx={{
                    width: { xs: 24, sm: 32, md: 44 },
                    px: { xs: 0, sm: 0, md: 0.25 },
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                  >
                    Del
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow
                  key={item.id}
                  hover
                  sx={{
                    opacity:
                      isShoppingListView && item.is_checked_off ? 0.6 : 1,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <TableCell padding="none" sx={{ textAlign: 'center' }}>
                    <Checkbox
                      checked={
                        isShoppingListView
                          ? item.is_checked_off
                          : item.is_on_list
                      }
                      onChange={() => handleCheckboxChange(item)}
                      size="small"
                      sx={{ p: { xs: 0, sm: 0.5 } }}
                    />
                  </TableCell>
                  <TableCell>
                    {editingId === item.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editValues.name}
                        onChange={e =>
                          setEditValues({ ...editValues, name: e.target.value })
                        }
                        autoFocus
                        sx={{
                          '& .MuiInputBase-root': {
                            py: { xs: 0, sm: 0.5 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={() => handleEdit(item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {item.name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === item.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editValues.location}
                        onChange={e =>
                          setEditValues({
                            ...editValues,
                            location: e.target.value,
                          })
                        }
                        sx={{
                          '& .MuiInputBase-root': {
                            py: { xs: 0, sm: 0.5 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={() => handleEdit(item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {item.location || '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {editingId === item.id ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editValues.price}
                        onChange={e =>
                          setEditValues({
                            ...editValues,
                            price: e.target.value,
                          })
                        }
                        type="number"
                        inputProps={{ step: '0.01' }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={() => handleEdit(item)}
                        sx={{
                          cursor: 'pointer',
                          fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        }}
                      >
                        {item.price ? `$${item.price}` : '-'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === item.id ? (
                      <Select
                        size="small"
                        fullWidth
                        value={editValues.quantity}
                        onChange={e =>
                          setEditValues({
                            ...editValues,
                            quantity: e.target.value,
                          })
                        }
                        sx={{
                          '& .MuiSelect-select': {
                            py: { xs: 0, sm: 0.5 },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          },
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                          <MenuItem key={num} value={num.toString()}>
                            {num}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={() => handleEdit(item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {item.quantity}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                    >
                      {item.subtotal ? `$${item.subtotal}` : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell padding="none" sx={{ textAlign: 'center' }}>
                    {editingId === item.id ? (
                      <IconButton
                        size="small"
                        onClick={handleSaveEdit}
                        color="primary"
                        sx={{ p: { xs: 0, sm: 0.5 } }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        color="error"
                        sx={{ p: { xs: 0, sm: 0.5 } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {/* New item row - only show in item management view */}
              {!isShoppingListView && (
                <TableRow>
                  <TableCell padding="none" sx={{ textAlign: 'center' }}>
                    <Checkbox
                      size="small"
                      disabled
                      sx={{ p: { xs: 0, sm: 0.5 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Item name..."
                      value={newItemName}
                      onChange={e => {
                        setNewItemName(e.target.value);
                        // Clear error when user starts typing
                        if (hasNameError) {
                          setHasNameError(false);
                          setErrorMessage(null);
                        }
                      }}
                      onKeyPress={e => {
                        if (e.key === 'Enter') handleAddItem();
                      }}
                      error={hasNameError}
                      helperText={
                        hasNameError ? 'This name is already in use' : ''
                      }
                      sx={{
                        '& .MuiInputBase-root': {
                          py: { xs: 0, sm: 0.5 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Location..."
                      value={newItemLocation}
                      onChange={e => setNewItemLocation(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') handleAddItem();
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          py: { xs: 0, sm: 0.5 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Price..."
                      value={newItemPrice}
                      onChange={e => setNewItemPrice(e.target.value)}
                      type="number"
                      inputProps={{ step: '0.01' }}
                      onKeyPress={e => {
                        if (e.key === 'Enter') handleAddItem();
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      fullWidth
                      value={newItemQuantity}
                      onChange={e => setNewItemQuantity(e.target.value)}
                      sx={{
                        '& .MuiSelect-select': {
                          py: { xs: 0, sm: 0.5 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        },
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                        <MenuItem key={num} value={num.toString()}>
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    -
                  </TableCell>
                  <TableCell padding="none" sx={{ textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={handleAddItem}
                      disabled={!newItemName.trim() || isCreating}
                      color="primary"
                      sx={{ p: { xs: 0, sm: 0.5 } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {items.length === 0 && (
          <Box py={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No items yet. Add your first item using the form above.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Item</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this item? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Checkboxes Confirmation Dialog */}
      <Dialog
        open={clearAllDialogOpen}
        onClose={() => setClearAllDialogOpen(false)}
        aria-labelledby="clear-all-dialog-title"
        aria-describedby="clear-all-dialog-description"
      >
        <DialogTitle id="clear-all-dialog-title">
          Clear All Checkboxes
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-all-dialog-description">
            Are you sure you want to clear all checkboxes? This will uncheck all
            items in the current view.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleClearAllCheckboxes} color="error" autoFocus>
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ItemManagementTable;
